/**
 * Supabase Trade Repository
 *
 * Implements ITradeRepository for Supabase storage.
 * Handles all trade CRUD, queries, and real-time subscriptions.
 *
 * @module repositories/supabase/SupabaseTradeRepository
 */
import { getSupabaseClient } from '../../services/auth/supabase';
import type { ITradeRepository, Unsubscribe } from '../interfaces/ITradeRepository';
import type { Trade, TradeInput, TradeUpdate, TradeStatus, TradeTags, ChartContext } from '../../types/trade';
import type { Database, Json } from '../../types/database.types';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { clientLogger } from '@/lib/clientLogger';

type TradeRow = Database['public']['Tables']['trades']['Row'];
type TradeInsert = Database['public']['Tables']['trades']['Insert'];
type TradeDbUpdate = Database['public']['Tables']['trades']['Update'];

// =============================================================================
// Row Conversion Helpers
// =============================================================================

/**
 * Convert database row to Trade domain object.
 */
function rowToTrade(row: TradeRow): Trade {
  return {
    id: row.id,
    userId: row.user_id,
    symbol: row.symbol,
    direction: row.direction as Trade['direction'],
    status: row.status as TradeStatus,
    entryPrice: row.entry_price,
    entryDate: row.entry_date,
    entrySize: row.entry_size,
    exitPrice: row.exit_price ?? undefined,
    exitDate: row.exit_date ?? undefined,
    fees: row.fees ?? 0,
    pnl: row.pnl ?? undefined,
    pnlPercent: row.pnl_percent ?? undefined,
    notes: row.notes ?? '',
    tags: (row.tags as unknown as TradeTags) ?? {},
    reviewedAt: row.reviewed_at ?? undefined,
    reviewNotes: row.review_notes ?? undefined,
    chartContext: (row.chart_context as unknown as ChartContext) ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

/**
 * Convert TradeInput to database insert row.
 */
function tradeInputToRow(userId: string, input: TradeInput): TradeInsert {
  return {
    user_id: userId,
    symbol: input.symbol,
    direction: input.direction,
    status: input.status,
    entry_price: input.entryPrice,
    entry_date: input.entryDate,
    entry_size: input.entrySize,
    exit_price: input.exitPrice ?? null,
    exit_date: input.exitDate ?? null,
    fees: input.fees ?? null,
    notes: input.notes ?? null,
    tags: (input.tags as unknown as Json) ?? null,
    reviewed_at: input.reviewedAt ?? null,
    review_notes: input.reviewNotes ?? null,
    chart_context: (input.chartContext as unknown as Json) ?? null,
    sentiment: null,
  };
}

/**
 * Calculate PnL for a trade.
 */
function calculatePnL(
  direction: Trade['direction'],
  entryPrice: number,
  exitPrice: number,
  size: number,
  fees: number
): { pnl: number; pnlPercent: number } {
  const multiplier = direction === 'long' ? 1 : -1;
  const pnl = (exitPrice - entryPrice) * size * multiplier - fees;
  const cost = entryPrice * size;
  const pnlPercent = cost > 0 ? (pnl / cost) * 100 : 0;
  return { pnl, pnlPercent };
}

// =============================================================================
// Repository Implementation
// =============================================================================

export class SupabaseTradeRepository implements ITradeRepository {
  // ===========================================================================
  // CRUD Operations
  // ===========================================================================

  async create(userId: string, input: TradeInput): Promise<Trade> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const insertData: TradeInsert = tradeInputToRow(userId, input);

    // Calculate PnL if trade is closed
    if (input.status === 'closed' && input.exitPrice !== undefined) {
      const { pnl, pnlPercent } = calculatePnL(
        input.direction,
        input.entryPrice,
        input.exitPrice,
        input.entrySize,
        input.fees ?? 0
      );
      insertData.pnl = pnl;
      insertData.pnl_percent = pnlPercent;
    }

    const { data, error } = await supabase
      .from('trades')
      .insert(insertData)
      .select()
      .single();

    if (error) throw new Error(`Failed to create trade: ${error.message}`);
    return rowToTrade(data);
  }

  async update(userId: string, tradeId: string, updates: TradeUpdate): Promise<Trade> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    // Build update object with snake_case keys
    const updateData: TradeDbUpdate = {};

    if (updates.symbol !== undefined) updateData.symbol = updates.symbol;
    if (updates.direction !== undefined) updateData.direction = updates.direction;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.entryPrice !== undefined) updateData.entry_price = updates.entryPrice;
    if (updates.entryDate !== undefined) updateData.entry_date = updates.entryDate;
    if (updates.entrySize !== undefined) updateData.entry_size = updates.entrySize;
    if (updates.exitPrice !== undefined) updateData.exit_price = updates.exitPrice;
    if (updates.exitDate !== undefined) updateData.exit_date = updates.exitDate;
    if (updates.fees !== undefined) updateData.fees = updates.fees;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.tags !== undefined) updateData.tags = updates.tags as unknown as Json;
    if (updates.reviewedAt !== undefined) updateData.reviewed_at = updates.reviewedAt;
    if (updates.reviewNotes !== undefined) updateData.review_notes = updates.reviewNotes;
    if (updates.chartContext !== undefined) updateData.chart_context = updates.chartContext as unknown as Json;

    // If closing a trade, calculate PnL
    if (updates.status === 'closed' && updates.exitPrice !== undefined) {
      // Fetch current trade to get entry details
      const { data: currentTrade, error: fetchError } = await supabase
        .from('trades')
        .select('*')
        .eq('id', tradeId)
        .eq('user_id', userId)
        .single();

      if (fetchError || !currentTrade) {
        throw new Error(`Failed to fetch trade: ${fetchError?.message ?? 'Not found'}`);
      }

      const entryPrice = updates.entryPrice ?? currentTrade.entry_price;
      const entrySize = updates.entrySize ?? currentTrade.entry_size;
      const direction = (updates.direction ?? currentTrade.direction) as Trade['direction'];
      const fees = updates.fees ?? currentTrade.fees ?? 0;

      const { pnl, pnlPercent } = calculatePnL(
        direction,
        entryPrice,
        updates.exitPrice,
        entrySize,
        fees
      );
      updateData.pnl = pnl;
      updateData.pnl_percent = pnlPercent;
    }

    updateData.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from('trades')
      .update(updateData)
      .eq('id', tradeId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw new Error(`Failed to update trade: ${error.message}`);
    return rowToTrade(data);
  }

  async delete(userId: string, tradeId: string): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase
      .from('trades')
      .delete()
      .eq('id', tradeId)
      .eq('user_id', userId);

    if (error) throw new Error(`Failed to delete trade: ${error.message}`);
  }

  async getById(userId: string, tradeId: string): Promise<Trade | null> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('trades')
      .select('*')
      .eq('id', tradeId)
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw new Error(`Failed to fetch trade: ${error.message}`);
    }

    return rowToTrade(data);
  }

  // ===========================================================================
  // Query Methods
  // ===========================================================================

  async getAll(userId: string): Promise<Trade[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', userId)
      .order('entry_date', { ascending: false });

    if (error) throw new Error(`Failed to fetch trades: ${error.message}`);
    return data.map(rowToTrade);
  }

  async getByDateRange(userId: string, startDate: string, endDate: string): Promise<Trade[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', userId)
      .gte('entry_date', startDate)
      .lte('entry_date', endDate)
      .order('entry_date', { ascending: false });

    if (error) throw new Error(`Failed to fetch trades by date range: ${error.message}`);
    return data.map(rowToTrade);
  }

  async getBySymbol(userId: string, symbol: string): Promise<Trade[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', userId)
      .eq('symbol', symbol.toUpperCase())
      .order('entry_date', { ascending: false });

    if (error) throw new Error(`Failed to fetch trades by symbol: ${error.message}`);
    return data.map(rowToTrade);
  }

  async getByStatus(userId: string, status: TradeStatus): Promise<Trade[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', userId)
      .eq('status', status)
      .order('entry_date', { ascending: false });

    if (error) throw new Error(`Failed to fetch trades by status: ${error.message}`);
    return data.map(rowToTrade);
  }

  async getOpenTrades(userId: string): Promise<Trade[]> {
    return this.getByStatus(userId, 'open');
  }

  async getRecentTrades(userId: string, limit: number): Promise<Trade[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase
      .from('trades')
      .select('*')
      .eq('user_id', userId)
      .order('entry_date', { ascending: false })
      .limit(limit);

    if (error) throw new Error(`Failed to fetch recent trades: ${error.message}`);
    return data.map(rowToTrade);
  }

  // ===========================================================================
  // Batch Operations
  // ===========================================================================

  async createMany(userId: string, inputs: TradeInput[]): Promise<Trade[]> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const insertData: TradeInsert[] = inputs.map((input) => {
      const row = tradeInputToRow(userId, input);

      // Calculate PnL for closed trades
      if (input.status === 'closed' && input.exitPrice !== undefined) {
        const { pnl, pnlPercent } = calculatePnL(
          input.direction,
          input.entryPrice,
          input.exitPrice,
          input.entrySize,
          input.fees ?? 0
        );
        row.pnl = pnl;
        row.pnl_percent = pnlPercent;
      }

      return row;
    });

    const { data, error } = await supabase
      .from('trades')
      .insert(insertData)
      .select();

    if (error) throw new Error(`Failed to create trades: ${error.message}`);
    return data.map(rowToTrade);
  }

  async deleteMany(userId: string, tradeIds: string[]): Promise<void> {
    const supabase = getSupabaseClient();
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase
      .from('trades')
      .delete()
      .eq('user_id', userId)
      .in('id', tradeIds);

    if (error) throw new Error(`Failed to delete trades: ${error.message}`);
  }

  // ===========================================================================
  // Real-time Subscriptions
  // ===========================================================================

  subscribe(userId: string, callback: (trades: Trade[]) => void): Unsubscribe {
    const supabase = getSupabaseClient();
    if (!supabase) {
      clientLogger.warn('[SupabaseTradeRepository] Cannot subscribe: Supabase not configured');
      callback([]);
      return () => {};
    }

    let channel: RealtimeChannel | null = null;

    // Initial fetch
    this.getAll(userId)
      .then(callback)
      .catch((err) => {
        clientLogger.error('[SupabaseTradeRepository] Initial fetch failed:', err);
        callback([]);
      });

    // Set up real-time subscription
    channel = supabase
      .channel(`trades:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trades',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Re-fetch all trades on any change
          this.getAll(userId)
            .then(callback)
            .catch((err) => clientLogger.error('[SupabaseTradeRepository] Refetch failed:', err));
        }
      )
      .subscribe();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }

  subscribeToOpenTrades(userId: string, callback: (trades: Trade[]) => void): Unsubscribe {
    const supabase = getSupabaseClient();
    if (!supabase) {
      clientLogger.warn('[SupabaseTradeRepository] Cannot subscribe: Supabase not configured');
      callback([]);
      return () => {};
    }

    let channel: RealtimeChannel | null = null;

    // Initial fetch
    this.getOpenTrades(userId)
      .then(callback)
      .catch((err) => {
        clientLogger.error('[SupabaseTradeRepository] Initial fetch failed:', err);
        callback([]);
      });

    // Set up real-time subscription for open trades
    channel = supabase
      .channel(`open-trades:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trades',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          // Re-fetch open trades on any change
          this.getOpenTrades(userId)
            .then(callback)
            .catch((err) => clientLogger.error('[SupabaseTradeRepository] Refetch failed:', err));
        }
      )
      .subscribe();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }
}
